import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useClient } from '../../context/ClientContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Building2, Sun, Moon, Bell, Menu, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = '';

export const DashboardLayout = ({ children, title }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { clients, selectedClientId, setSelectedClientId, selectedClient } = useClient();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
        const newUnread = data.filter(n => !n.read).length;
        // Browser push notification for new items
        if (newUnread > unreadCount && unreadCount > 0 && Notification.permission === 'granted') {
          const newest = data.find(n => !n.read);
          if (newest) new Notification('Nueva notificacion', { body: newest.message, icon: '/favicon.ico' });
        }
        setUnreadCount(newUnread);
      }
    } catch {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, []);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/read-all`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteAllNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setNotifications([]);
      setUnreadCount(0);
      toast.success('Notificaciones eliminadas');
    } catch {}
  };

  const deleteNotification = async (notifId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/${notifId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.filter(n => n.notification_id !== notifId));
    } catch {}
  };

  const handleNotifClick = async (n) => {
    // Mark this notification as read
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/read-all`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    setNotifOpen(false);

    // Navigate to the correct page with highlight info
    switch (n.type) {
      case 'chat_message':
        navigate(`/chat?highlight=dm_${n.reference_id}`);
        break;
      case 'task_assigned':
      case 'task_forwarded':
        navigate(`/tasks?highlight=${n.reference_id}`);
        break;
      case 'content_assigned':
      case 'content_proposal':
        navigate(`/content?highlight=${n.reference_id}`);
        break;
      case 'work_order':
        navigate(`/work-orders?highlight=${n.reference_id}`);
        break;
      default:
        navigate('/dashboard');
    }
  };

  const isDark = theme === 'dark';

  const getNotifColor = (type) => {
    switch (type) {
      case 'chat_message': return 'border-l-blue-500';
      case 'task_assigned': case 'task_forwarded': return 'border-l-orange-500';
      case 'content_assigned': case 'content_proposal': return 'border-l-purple-500';
      case 'work_order': return 'border-l-emerald-500';
      default: return 'border-l-zinc-500';
    }
  };

  return (
    <div className={cn('min-h-screen transition-colors', isDark ? 'bg-[#0A0A0A]' : 'bg-[#F5F5F5]')}>
      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} onMobileClose={() => setMobileOpen(false)} mobileOpen={mobileOpen} />
      
      <div className={cn('transition-all duration-300', collapsed ? 'lg:ml-20' : 'lg:ml-64')}>
        <header className={cn('sticky top-0 z-30 border-b px-4 lg:px-6 py-3', isDark ? 'bg-[#0A0A0A]/80 border-white/5 backdrop-blur-xl' : 'bg-white/80 border-zinc-200 backdrop-blur-xl')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={20} /></Button>
              <h1 className={cn('text-lg lg:text-xl font-semibold', isDark ? 'text-white' : 'text-zinc-900')} data-testid="page-title">{title}</h1>
            </div>
            <div className="flex items-center gap-2 lg:gap-3">
              {clients.length > 0 && user?.role !== 'client' && (
                <Select value={selectedClientId || 'all'} onValueChange={v => setSelectedClientId(v === 'all' ? null : v)}>
                  <SelectTrigger className={cn('w-[140px] lg:w-[180px] h-9', isDark ? 'bg-[#141414] border-white/10 text-white' : 'bg-white border-zinc-200 text-zinc-900')} data-testid="client-selector">
                    <Building2 size={14} className="mr-1 text-orange-400 shrink-0" /><SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>
                    <SelectItem value="all">Todos los clientes</SelectItem>
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="ghost" size="icon" onClick={toggleTheme} className={isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500'} data-testid="theme-toggle">
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </Button>
              <div className="relative">
                <Button variant="ghost" size="icon" onClick={() => { setNotifOpen(true); markAllRead(); }} className={cn('relative', isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500')} data-testid="notifications-btn">
                  <Bell size={18} />
                  {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
          <DialogContent className={cn('max-w-md max-h-[70vh] overflow-y-auto', isDark ? 'bg-[#1A1A1A] border-white/10 text-white' : 'bg-white')}>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Notificaciones</span>
                <div className="flex gap-1">
                  {notifications.length > 0 && <Button variant="ghost" size="sm" onClick={deleteAllNotifications} className="text-red-400 hover:text-red-300 text-xs" data-testid="clear-all-notifs"><Trash2 size={14} className="mr-1" />Borrar todo</Button>}
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-1 mt-2">
              {notifications.length === 0 && <p className="text-center text-zinc-500 py-6 text-sm">Sin notificaciones</p>}
              {notifications.map(n => (
                <div key={n.notification_id} onClick={() => handleNotifClick(n)} className={cn('flex items-start gap-3 p-3 rounded-lg cursor-pointer border-l-4 transition-colors', getNotifColor(n.type), !n.read ? (isDark ? 'bg-orange-500/10' : 'bg-orange-50') : '', isDark ? 'hover:bg-white/5' : 'hover:bg-zinc-50')} data-testid={`notif-${n.notification_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-sm', isDark ? 'text-zinc-200' : 'text-zinc-800', !n.read && 'font-medium')}>{n.message}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{n.created_at ? format(new Date(n.created_at), "dd MMM HH:mm", { locale: es }) : ''}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-zinc-500 hover:text-red-400" onClick={(e) => deleteNotification(n.notification_id, e)}><X size={12} /></Button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
